/**
 * Phase 8c — Maverick Intervention Resolver
 *
 * Maps a PredictedFailure to an executable modal flow when one is available,
 * or returns a route fallback otherwise. Lives at the rendering/runtime
 * layer so the engine (preMortemEngine.ts) stays a pure scoring function —
 * the intervention shape on the engine side is unchanged.
 *
 * Convertible modes (5 of 10) — open existing modals with context prefilled:
 *   - material_will_miss_install      → OrderingModal
 *   - permit_will_block_schedule      → LogInteractionModal (draft prefilled)
 *   - customer_about_to_escalate      → LogInteractionModal (draft prefilled)
 *   - crew_unreliable_for_install     → QuickAssignModal
 *   - unresolved_critical_issue       → QuickResolveIssueModal
 *
 * Partial / blocked modes (5 of 10) fall back to navigation via the route
 * already on the engine intervention.
 *
 * Hard constraints honoured:
 *   - no new runners
 *   - no new modal components
 *   - no preMortemEngine.ts contract changes
 */

import type { PredictedFailure } from "@/data/maverick/preMortemEngine";
import type { Job, Issue } from "@/data/types";

export type MaverickModalKind =
  | "ordering"
  | "logInteraction"
  | "quickAssign"
  | "quickResolveIssue"
  | "markFollowUpsDone";

export interface MaverickInterventionPlan {
  /** "modal" → MaverickLayout opens the named modal with payload.
   *  "route" → fall back to navigate(intervention.route). */
  mode: "modal" | "route";
  modal?: MaverickModalKind;
  /** Per-modal payload — typed loosely here, narrowed at the call site. */
  payload?: {
    job?: Job;
    openIssues?: Issue[];
    issue?: Issue | null;
    customerName?: string;
    relatedJobId?: string;
    defaultNote?: string;
  };
  /** Always carry the original route so the layout can fall back if the
   *  modal kind can't actually be opened (e.g. missing job). */
  fallbackRoute?: string;
}

export interface ResolveContext {
  jobs: Job[];
  issues: Issue[];
}

/**
 * Resolve a prediction into an execution plan. Pure, no side effects.
 * Always returns a plan — never throws — so the caller can rely on the
 * route fallback when modal context is incomplete.
 */
export function resolveMaverickIntervention(
  prediction: PredictedFailure,
  ctx: ResolveContext,
): MaverickInterventionPlan {
  const job = ctx.jobs.find((j) => j.id === prediction.jobId);
  const route = prediction.intervention.route;

  // Without a job, we can't safely open any of the 4 modals — fall back.
  if (!job) {
    return { mode: "route", fallbackRoute: route };
  }

  switch (prediction.failureMode) {
    case "material_will_miss_install":
      return {
        mode: "modal",
        modal: "ordering",
        payload: {
          job,
          openIssues: ctx.issues.filter(
            (i) => i.jobId === job.id && (i.status === "Open" || i.status === "In Progress"),
          ),
        },
        fallbackRoute: route,
      };

    case "crew_unreliable_for_install":
      return {
        mode: "modal",
        modal: "quickAssign",
        payload: {
          job,
          openIssues: ctx.issues.filter(
            (i) => i.jobId === job.id && (i.status === "Open" || i.status === "In Progress"),
          ),
        },
        fallbackRoute: route,
      };

    case "unresolved_critical_issue": {
      // Pick the highest-severity open issue for this job.
      const open = ctx.issues
        .filter((i) => i.jobId === job.id && (i.status === "Open" || i.status === "In Progress"))
        .sort((a, b) => (a.severity === "Urgent" ? -1 : 1) - (b.severity === "Urgent" ? -1 : 1));
      const issue = open[0] ?? null;
      if (!issue) {
        // No issue to resolve → route fallback (engine and reality disagree).
        return { mode: "route", fallbackRoute: route };
      }
      return {
        mode: "modal",
        modal: "quickResolveIssue",
        payload: { issue },
        fallbackRoute: route,
      };
    }

    case "customer_about_to_escalate":
    case "permit_will_block_schedule":
    case "payment_not_collected_post_completion": {
      const draft = prediction.intervention.draft;
      // Communication-based interventions need a draft to be worth a modal.
      // If for some reason none is present, fall back to route.
      if (!draft) {
        return { mode: "route", fallbackRoute: route };
      }
      return {
        mode: "modal",
        modal: "logInteraction",
        payload: {
          customerName: job.customerName,
          relatedJobId: job.id,
          defaultNote: draft,
        },
        fallbackRoute: route,
      };
    }

    // followup_dropping_ball — inline Mark Done flow. The actual list of
    // overdue follow-ups is sourced from FollowUpTask state at the call site
    // (MaverickLayout) since the resolver doesn't take followUpTasks in ctx.
    // Payload only carries the job for context display.
    case "followup_dropping_ball":
      return {
        mode: "modal",
        modal: "markFollowUpsDone",
        payload: { job },
        fallbackRoute: route,
      };

    // Partial / blocked modes — keep route-based per Pass A audit.
    case "install_will_callback":
    case "financial_will_block_closeout":
    case "readiness_degrading":
    case "duplicate_attention_chaos":
    default:
      return { mode: "route", fallbackRoute: route };
  }
}
